import { CalendarDate, IPlacement } from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { Header } from "components/header";
import { AutosavingInput } from "components/ui/autosaving-input";
import { CalendarDateInput } from "components/ui/calendar-date";
import { Checkbox } from "components/ui/checkbox";
import React from "react";
import { useFeature } from "store/selectors/hooks";
import { ClientDisclosureSummary } from "./client-disclosure-summary";

interface ClientAgreementSectionProps {
    placement: IPlacement;
    onFieldChange: (fieldName: string, newValue: string | boolean) => void;
    onSendDisclosure: () => void;
}

export const ClientAgreementSection: React.FC<ClientAgreementSectionProps> = props => {
    const { placement, onFieldChange, onSendDisclosure } = props;

    const disclosureEnabled = useFeature("clientDisclosures");

    return (
        <InputFormBlock>
            <Header iconName="44_SignedAgreement">Client agreement</Header>
            <div className="space-y-4">
                <div>
                    <Header headerSize="minor">Prior history</Header>
                    <div className="vertically-spaced-children">
                        <Checkbox
                            checked={placement.contactedOtherAgency}
                            onChange={e =>
                                onFieldChange("contactedOtherAgency", e.target.checked)
                            }
                            label="Already contacted another agency"
                        />
                        <Checkbox
                            checked={placement.touredPreviously}
                            onChange={e =>
                                onFieldChange("touredPreviously", e.target.checked)
                            }
                            label="Toured prior to contacting us"
                        />
                        {placement.contactedOtherAgency ? (
                            <AutosavingInput
                                multiLine={true}
                                label="Other Agencies Contacted (Notes)"
                                initial={placement.contactedOtherAgencyNotes}
                                onCommit={newVal =>
                                    onFieldChange("contactedOtherAgencyNotes", newVal)
                                }
                            />
                        ) : null}
                        {placement.touredPreviously ? (
                            <AutosavingInput
                                multiLine={true}
                                label="Prior Tour Notes"
                                initial={placement.touredPreviouslyNotes}
                                onCommit={newVal =>
                                    onFieldChange("touredPreviouslyNotes", newVal)
                                }
                            />
                        ) : null}
                    </div>
                </div>
                {disclosureEnabled ? (
                    <ClientDisclosureSummary
                        clientId={placement.id}
                        mailShowing={false}
                        onSendDisclosure={onSendDisclosure}
                    />
                ) : (
                    <div className="vertically-spaced-children">
                        <Header headerSize="minor">Agency agreement / disclosure</Header>
                        <div className="vertically-spaced-children">
                            <Checkbox
                                checked={placement.signedAgencyAgreement}
                                onChange={e =>
                                    onFieldChange(
                                        "signedAgencyAgreement",
                                        e.target.checked,
                                    )
                                }
                                label="Signed agreement / received our disclosure"
                            />
                            {placement.signedAgencyAgreement ? (
                                <>
                                    <div>
                                        <CalendarDateInput
                                            label="Date signed"
                                            initial={placement.agencyAgreementDate}
                                            onChange={(newVal: CalendarDate) =>
                                                onFieldChange(
                                                    "agencyAgreementDate",
                                                    newVal ? newVal.toString() : null,
                                                )
                                            }
                                        />
                                    </div>
                                    <AutosavingInput
                                        label="Agreement notes"
                                        multiLine={true}
                                        initial={placement.agreementNotes}
                                        onCommit={newVal =>
                                            onFieldChange("agreementNotes", newVal)
                                        }
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>
        </InputFormBlock>
    );
};
